import { NumberParserOptions } from "globalize/dist/globalize/number";
import { numberParser, formatNumber } from "./globalize-util";
import _ from "lodash";

export function safeParseFloat(value: any, defaultValue: number): number | undefined {
    const parsedValue = parseFloat(value);

    return isNaN(parsedValue) || !isFinite(parsedValue) ? defaultValue : parsedValue;
}

export function safeParseInt(value: any, defaultValue: number, radix: number = 10): number | undefined {
    const parsedValue = parseInt(value, radix);

    return isNaN(parsedValue) || !isFinite(parsedValue) ? defaultValue : parsedValue;
}

/**
 * Formats the thousands separator.
 * Use safeFormatNumber which uses the right separator per locale. For example, Portuguese uses . as separator.
 * @param value
 * @param separator
 * @returns
 */
export function formatThousands(value: number | string, separator = ",") {
    const [integer, decimals] = value.toString().split(".");
    return integer.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${separator}`) + (decimals ? `.${decimals}` : "");
}

/**
 * Formats decimals rendering.
 * Majority of use case is to add trailing 0 when decimal count is lower than specified
 * @param value
 * @param separator
 * @returns
 */
export function formatDecimals(value: number | string, decimalCount: number = 0) {
    return Number(value || 0).toFixed(decimalCount);
}

/**
 * Safe parses the float and returns in localized format.
 * Prefer using <FormatNumber> react component over this.
 * @param value
 * @param maximumFractionDigits - default is 2
 * @returns - formatted number of 0.
 */
export function safeFormatNumber(
    value: number | string,
    options: { maximumFractionDigits: number; minimumFractionDigits?: number } = { maximumFractionDigits: 2 },
) {
    const parsedFloat = safeParseFloat(value, null);
    if (!_.isNil(parsedFloat)) {
        return formatNumber(value, options);
    }
    return "0";
}

// Check if value is a valid number or string number.
export function isValidNumber(value: any): boolean {
    return !isNaN(value) && !!String(value).trim();
}

export function parseNumber(value: string, options?: NumberParserOptions) {
    return numberParser(options)(value?.trim());
}

export function distributeEvenly(numToDistribute: number, distributionCount: number): number[] {
    numToDistribute = Math.floor(numToDistribute ?? 0);
    distributionCount = Math.floor(distributionCount);
    const minEachDistribution = Math.floor(numToDistribute / distributionCount);
    const distribution = Array(distributionCount)
        .fill(null)
        .map(() => minEachDistribution);
    const remainder = numToDistribute - minEachDistribution * distributionCount;
    Array(remainder)
        .fill(null)
        .forEach((v, index) => {
            distribution[index] += 1;
        });

    return distribution;
}

export const roundValue = (num, decimals = 2) => {
    const p = Math.pow(10, decimals);
    const n = num * p * (1 + Number.EPSILON);
    return Math.round(n) / p;
};
